<!-- Empty State -->
@if (allTrips.length === 0) {
  <div class="flex flex-col items-center justify-center gap-4 py-16">
    <div
      class="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100"
    >
      <lucide-icon name="search-x" class="h-8 w-8 text-primary-500" />
    </div>
    <div class="flex flex-col items-center gap-1 text-center">
      <span class="text-lg font-semibold text-gray-800">{{
        "travelBoards.noFilterResults" | transloco
      }}</span>
      <span class="text-gray-500">{{
        "travelBoards.noFilterResultsExplanation" | transloco
      }}</span>
    </div>
  </div>
} @else {
  <!-- Trips Grid -->
  <section class="flex flex-wrap gap-4 px-4 md:px-0">
    @for (trip of trips; track trip.id) {
      <article
        class="group flex w-full max-w-sm flex-col overflow-hidden rounded-lg bg-white shadow-sm transition-shadow duration-300 hover:shadow-md sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)] xl:w-[calc(25%-0.75rem)]"
      >
        <!-- Trip Header -->
        <header class="relative flex flex-col">
          <button
            class="overflow-hidden"
            (click)="
              trip.isDraft
                ? travelBoardService.editTrip(trip.id!)
                : travelBoardService.viewTrip(trip.id!)
            "
          >
            @if (trip.imagePath) {
              <img
                [src]="getImageUrl(trip.imagePath)"
                [alt]="trip.title"
                class="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            } @else {
              <div
                class="flex h-48 w-full items-center justify-center bg-greyscale-100"
              >
                <div
                  class="flex h-16 w-16 items-center justify-center rounded-full bg-primary-200 transition-transform duration-300 group-hover:scale-110"
                >
                  <app-greentripper-pin-icon
                    styleClass="w-8 h-8 text-primary-500 ml-[4px]"
                  />
                </div>
              </div>
            }
            @if (trip.isDraft) {
              <span class="absolute right-2 top-2 z-10">
                <span
                  class="inline-flex items-center rounded bg-primary-100 px-2 py-1 text-xs font-semibold uppercase text-primary-700"
                >
                  {{ "travelBoards.draft" | transloco }}
                </span>
              </span>
            }
            @if (trip.hasValidationErrors) {
              <span
                class="absolute left-2 top-2 z-10 inline-flex items-center justify-center rounded bg-red-100 p-1 text-red-700"
                [pTooltip]="'travelBoards.validationErrors' | transloco"
                tooltipPosition="top"
              >
                <lucide-icon name="triangle-alert" class="h-4 w-4" />
              </span>
            }
          </button>

          <div class="flex flex-grow flex-row items-start justify-between p-4">
            <button
              class="flex flex-col text-left"
              (click)="
                trip.isDraft
                  ? travelBoardService.editTrip(trip.id!)
                  : travelBoardService.viewTrip(trip.id!)
              "
            >
              <h2
                class="line-clamp-2 text-lg font-medium text-greyscale-800 transition-colors hover:text-primary-600"
                [pTooltip]="trip.title"
                tooltipPosition="top"
                [tooltipOptions]="{ showDelay: 500 }"
              >
                {{ trip.title }}
              </h2>
              <h3 class="text-sm font-medium text-primary-500">
                {{ trip.countryName }}
              </h3>
            </button>

            <div
              class="ml-2 flex items-start opacity-0 transition-opacity group-hover:opacity-100 [&_button]:!h-8 [&_button]:!w-8 [&_button]:!p-0"
            >
              <app-actions [options]="travelBoardService.getActions(trip)" />
            </div>
          </div>
        </header>

        <div class="grid flex-grow grid-cols-2 grid-rows-2 gap-3 px-4 pb-4">
          <div>
            <h3
              class="text-xs font-medium uppercase tracking-wide text-greyscale-500"
            >
              {{ "travelBoards.duration" | transloco }}
            </h3>
            <p class="text-sm text-greyscale-700">
              {{ trip.days }}
              <span class="lowercase">{{
                (trip.days > 1 ? "travelBoards.days" : "travelBoards.day")
                  | transloco
              }}</span>
            </p>
          </div>

          <div>
            <h3
              class="text-xs font-medium uppercase tracking-wide text-greyscale-500"
            >
              {{ "travelBoards.travellers" | transloco }}
            </h3>
            <p class="text-sm text-greyscale-700">
              {{ trip.travellers }}
              <span class="lowercase">{{
                (trip.travellers > 1
                  ? "travelBoards.people"
                  : "travelBoards.person"
                ) | transloco
              }}</span>
            </p>
          </div>

          <div>
            <h3
              class="text-xs font-medium uppercase tracking-wide text-greyscale-500"
            >
              {{ "travelBoards.groupImpact" | transloco }}
            </h3>
            <p class="text-sm text-greyscale-700">
              {{ trip.totalImpact | formatCO2e }}
            </p>
          </div>
          <div>
            <h3
              class="text-xs font-medium uppercase tracking-wide text-greyscale-500"
            >
              {{ "travelBoards.impactPerTraveller" | transloco }}
            </h3>
            <p class="text-sm text-greyscale-700">
              {{ trip.impactPerTraveller | formatCO2e }}
            </p>
          </div>
        </div>

        <!-- Partner Info -->
        <footer
          class="mt-auto flex flex-row items-center border-t border-greyscale-100 px-4 py-3"
        >
          <div class="flex min-h-0 flex-grow flex-wrap gap-2">
            @for (client of trip.companyClients; track $index) {
              <!-- Variante pour l'affichage du nom du client -->
              <!-- <p-tag [value]="client" class="text-sm" severity="secondary" /> -->
              <app-client-avatar [clientName]="client" />
            }
          </div>
          @if (trip.isDraft) {
            <p-button
              severity="secondary"
              (onClick)="travelBoardService.editTrip(trip.id!)"
              [label]="'travelBoards.continueEditing' | transloco"
            />
          } @else {
            <app-ecoscore [ecoScore]="trip.ecoScore" />
          }
        </footer>
      </article>
    }
  </section>
  <div class="mt-8">
    <p-paginator
      (onPageChange)="onPageChange($event)"
      [first]="page.first!"
      [rows]="page.rows"
      [totalRecords]="this.allTrips.length"
      [rowsPerPageOptions]="[12, 24, 48]"
      dropdownAppendTo="body"
    />
  </div>
}
